"use client";

import { FormEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import { usePostHog } from "posthog-js/react";
import { sendGTMEvent } from "@next/third-parties/google";
import { submitFormDataToZapier } from "@/helpers/zapier";
import { useAffiliateWebhook } from "@/components/global/AffiliateWebhookProvider";
import { cn } from "@/lib/utils";
import { IZapierForm } from "@/sanity/globals/ZapierForm";
import { CAP_TRIM, HN, QeCta, QeMedal } from "./kit/QeKit";

/* ── Sign-up pop-up ───────────────────────────────────────────────────────────
   Figma RTqa1425vVOUAEVlKfKcnt "New adjustments" — desktop 12505:24764 (card 480 wide), mobile 12505:24794 (358).
   Every "#signup" CTA on the page opens it over a black 62 % veil with a 10 px blur. First name + email go to the
   IQ Launch Page Zap (Marcel 26/10), then the visitor lands on the thank-you page (this path + /thank-you, query
   kept). With "Forward affiliate to webhook" on, the URL's ?aff= goes along as affiliate_code (same as the event
   page). Esc, the ✕ and a click on the veil close it. */

const WEBHOOK = "https://hooks.zapier.com/hooks/catch/16907183/493q0al/";
const pad = (n: number) => String(n).padStart(2, "0");

type Field = "firstName" | "email";

function useStartsIn(target?: string) {
  const ms = target ? Date.parse(stegaClean(target)) : NaN;
  const [parts, setParts] = useState([0, 0, 0, 0]);
  useEffect(() => {
    if (Number.isNaN(ms)) return;
    const tick = () => {
      const s = Math.floor(Math.max(0, ms - Date.now()) / 1000);
      setParts([Math.floor(s / 86400), Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), s % 60]);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [ms]);
  return parts;
}

export default function QeSignupPopup({ eventDate }: { eventDate?: string }) {
  const postHog = usePostHog();
  const forwardAffiliate = useAffiliateWebhook();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<"idle" | "sending">("idle");
  const [error, setError] = useState<Field | "submit" | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const [d, h, m, s] = useStartsIn(eventDate);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
    trigger.current?.focus();
  }, []);

  // every "#signup" CTA opens the pop-up instead of jumping to the final section
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.('a[href="#signup"]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      trigger.current = a as HTMLElement;
      setOpen(true);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const root = document.documentElement, prev = root.style.overflow;
    root.style.overflow = "hidden";
    firstRef.current?.focus({ preventScroll: true });
    return () => { root.style.overflow = prev; };
  }, [open]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") return close();
    if (e.key !== "Tab" || !cardRef.current) return;
    // keep the focus inside the card
    const f = cardRef.current.querySelectorAll<HTMLElement>("input, button");
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const el = e.currentTarget.elements;
    const firstInput = el.namedItem("firstName") as HTMLInputElement, emailInput = el.namedItem("email") as HTMLInputElement;
    const firstName = firstInput.value.trim(), email = emailInput.value.trim();
    if (!firstName) { setError("firstName"); firstInput.focus(); return; }
    if (!email || !emailInput.checkValidity()) { setError("email"); emailInput.focus(); return; }
    setError(null);
    setState("sending");
    const affiliateCode = forwardAffiliate ? new URLSearchParams(window.location.search).get("aff") : null;
    const fields: Record<string, string> = { firstName, email, ...(affiliateCode ? { affiliate_code: affiliateCode } : {}) };
    postHog?.identify(email, { firstName });
    postHog?.capture("form_submit", { form: "quarterly-event-signup", fields });
    const res = await submitFormDataToZapier(fields, { settings: { url: WEBHOOK } } as IZapierForm);
    if (!res.success) { setState("idle"); setError("submit"); return; }
    sendGTMEvent({ event: "zapierFormSubmit", "zapier-form-url": WEBHOOK, pageName: document.title, pagePath: window.location.href, "form-firstname": firstName, "form-email": email });
    window.location.assign(`${window.location.pathname.replace(/\/+$/, "")}/thank-you${window.location.search}`);
  }

  if (!open) return null;

  const input = (f: Field) =>
    cn(
      "h-[6.5rem] w-full rounded-[0.5rem] border border-solid bg-white/[0.04] px-[2rem] font-geist text-[max(16px,2rem)] leading-[3rem] text-white outline-none transition-colors duration-200 placeholder:text-white/45 lg:text-[2rem]",
      "border-white/[0.12] focus:border-[rgba(158,227,176,0.6)] focus:bg-[rgba(158,227,176,0.05)]",
      error === f && "border-[rgba(229,72,77,0.7)] focus:border-[rgba(229,72,77,0.7)]",
    );

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto px-[2rem] py-[4rem]" onKeyDown={onKeyDown}>
      <div aria-hidden="true" className="fixed inset-0 bg-black/[0.62] backdrop-blur-[10px] animate-in fade-in duration-200" onClick={close} />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qe-signup-title"
        className="relative flex w-full flex-col items-center gap-[4rem] overflow-hidden rounded-[1rem] border border-solid border-white/10 bg-[#0c0d0c] px-[2rem] pt-[4.5rem] pb-[2rem] text-white animate-in fade-in zoom-in-95 duration-300 lg:w-[60rem] lg:px-[4rem] lg:pt-[5rem] lg:pb-[4rem]">
        {/* header */}
        <div className="flex w-full flex-col items-center gap-[2rem]">
          <div className="flex items-center gap-[1.75rem]">
            <QeMedal className="h-[5.5446rem] w-[4.8054rem]" />
            <p className={cn(CAP_TRIM, "mb-0 font-geist text-[2.25rem] leading-[3rem] font-medium tracking-[-0.01em] whitespace-nowrap text-white")}>NOV 24 · LIVE PREMIERE</p>
          </div>
          <div className="flex w-full flex-col items-center gap-[2.25rem] text-center lg:gap-[3rem]">
            <h2 id="qe-signup-title" className={cn(HN, CAP_TRIM, "mb-0 text-[3.5rem] leading-[5rem] whitespace-nowrap text-white lg:text-[4.5rem]")}>
              <span className="block [font-family:inherit]">Save your free seat</span>
              <span className="block [font-family:inherit]">for the live premiere.</span>
            </h2>
            <p className={cn(CAP_TRIM, "mb-0 w-[27.75rem] font-geist text-[2rem] leading-[3rem] font-normal text-white/60 lg:w-[33.5rem]")}>
              The top 1% open their strategies live.<br className="hidden lg:inline" /> One night only.
            </p>
          </div>
        </div>

        {/* form + countdown */}
        <div className="flex w-full flex-col items-center gap-[2.5rem]">
          <form noValidate onSubmit={onSubmit} aria-busy={state === "sending"} className="flex w-full flex-col gap-[1rem]">
            <input ref={firstRef} name="firstName" type="text" autoComplete="given-name" placeholder="First name" aria-label="First name" aria-invalid={error === "firstName" || undefined} className={input("firstName")} onInput={() => error === "firstName" && setError(null)} />
            <input name="email" type="email" autoComplete="email" inputMode="email" placeholder="Email address" aria-label="Email address" aria-invalid={error === "email" || undefined} className={input("email")} onInput={() => error === "email" && setError(null)} />
            <QeCta submit disabled={state === "sending"} label="Sign Up for Free" className="flex w-full" mainClassName="flex-1" />
            {error === "submit" && <p role="alert" className="mb-0 text-center font-geist text-[1.75rem] leading-[2.5rem] text-[#e5484d]">Something went wrong. Please try again.</p>}
          </form>
          <div className="flex items-center gap-[1.5rem]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/quarterly-event/popup/dot.svg" alt="" className="h-[0.75rem] w-[0.75rem] max-w-none" />
            <p className="mb-0 font-geist text-[1.75rem] leading-[2.5rem] font-medium whitespace-nowrap text-white/50">STARTS IN</p>
            <div className="flex items-center gap-[0.75rem]">
              {([[d, "d"], [h, "h"], [m, "m"], [s, "s"]] as const).map(([v, u]) => (
                <p key={u} className="mb-0 rounded-[0.5rem] border border-solid border-white/10 bg-white/5 px-[0.75rem] py-[0.5rem] font-geist text-[1.75rem] leading-[2.5rem] font-medium whitespace-nowrap text-white">
                  {pad(v)}<span className="font-geist text-[#9ee3b0]">{u}</span>
                </p>
              ))}
            </div>
          </div>
        </div>

        <button type="button" aria-label="Close" onClick={close} className="absolute top-0 right-[-0.125rem] flex h-[5rem] w-[5rem] cursor-pointer appearance-none items-center justify-center rounded-[0.5rem] border-0 bg-transparent p-0 opacity-100 transition-opacity hover:opacity-70">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/quarterly-event/popup/close.svg" alt="" className="h-[2.5rem] w-[2.5rem] max-w-none" />
        </button>
      </div>
    </div>
  );
}
